import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddCircleOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import type { RoleDto } from '@/types/administration';
import type { UserDto } from '@/types/auth';
import type {
  ApprovalMatrixDto,
  CreateApprovalLevelRequest,
  CreateApprovalMatrixRequest,
  UpdateApprovalMatrixRequest,
} from '@/types/approvals';
import { TRANSACTION_TYPES } from '@/types/approvals';

const emptyLevel = (levelNo: number): CreateApprovalLevelRequest => ({
  levelNo, name: `Level ${levelNo}`, approverType: 'Role', approverRoleId: null, approverUserId: null,
});

export default function ApprovalMatrixPage() {
  const { t } = useTranslation('approvalMatrix');
  const queryClient = useQueryClient();

  const [transactionType, setTransactionType] = useState<string>(TRANSACTION_TYPES[0]);

  const { data: matrices = [] } = useQuery({
    queryKey: ['approval-matrices'],
    queryFn: async () => (await apiClient.get<ApprovalMatrixDto[]>('/approval-matrices')).data,
  });
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await apiClient.get<RoleDto[]>('/roles')).data,
  });
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await apiClient.get<UserDto[]>('/users')).data,
  });

  const existing = matrices.find((m) => m.transactionType === transactionType) ?? null;

  const [thresholdValue, setThresholdValue] = useState<string>('');
  const [hierarchyMode, setHierarchyMode] = useState<'Sequential' | 'AnyOne'>('Sequential');
  const [slaHours, setSlaHours] = useState(24);
  const [autoNotify, setAutoNotify] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [levels, setLevels] = useState<CreateApprovalLevelRequest[]>([emptyLevel(1)]);

  useEffect(() => {
    if (existing) {
      setThresholdValue(existing.thresholdValue?.toString() ?? '');
      setHierarchyMode(existing.hierarchyMode);
      setSlaHours(existing.slaHours);
      setAutoNotify(existing.autoNotify);
      setIsActive(existing.isActive);
      setLevels(
        existing.levels.map((l) => ({
          levelNo: l.levelNo, name: l.name, approverType: l.approverType,
          approverRoleId: l.approverRoleId, approverUserId: l.approverUserId,
        })),
      );
    } else {
      setThresholdValue('');
      setHierarchyMode('Sequential');
      setSlaHours(24);
      setAutoNotify(true);
      setIsActive(true);
      setLevels([emptyLevel(1)]);
    }
  }, [existing, transactionType]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const normalizedLevels = levels.map((l, idx) => ({ ...l, levelNo: idx + 1 }));
      if (existing) {
        const payload: UpdateApprovalMatrixRequest = {
          thresholdValue: thresholdValue ? Number(thresholdValue) : null,
          hierarchyMode, slaHours, autoNotify, isActive, levels: normalizedLevels,
        };
        return apiClient.put(`/approval-matrices/${existing.id}`, payload);
      }
      const payload: CreateApprovalMatrixRequest = {
        transactionType, thresholdValue: thresholdValue ? Number(thresholdValue) : null,
        hierarchyMode, slaHours, autoNotify, levels: normalizedLevels,
      };
      return apiClient.post('/approval-matrices', payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['approval-matrices'] }),
  });

  const addLevel = () => setLevels((prev) => [...prev, emptyLevel(prev.length + 1)]);
  const removeLevel = (idx: number) => setLevels((prev) => prev.filter((_, i) => i !== idx));
  const updateLevel = (idx: number, patch: Partial<CreateApprovalLevelRequest>) =>
    setLevels((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const levelLabel = (l: CreateApprovalLevelRequest) => {
    if (l.approverType === 'Role') return roles.find((r) => r.id === l.approverRoleId)?.name ?? t('selectRole');
    return users.find((u) => u.id === l.approverUserId)?.fullName ?? t('selectUser');
  };

  const missingHighValueL3 = useMemo(
    () => transactionType === 'PurchaseOrder' && Number(thresholdValue || 0) > 0 && levels.length < 3,
    [transactionType, thresholdValue, levels.length],
  );

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.APPROVAL_MATRIX_MANAGE}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {t('saveMatrix')}
            </Button>
          </IfPermitted>
        }
      />

      <GridContainer spacing={2} columns={12}>
        <GridItem xs={12} md={8}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <GridContainer spacing={2} columns={2}>
                  <GridItem xs={1}>
                    <TextField select fullWidth label={t('transactionType')} value={transactionType} onChange={(e) => setTransactionType(e.target.value)}>
                      {TRANSACTION_TYPES.map((tt) => (
                        <MenuItem key={tt} value={tt}>
                          {tt}
                        </MenuItem>
                      ))}
                    </TextField>
                  </GridItem>
                  <GridItem xs={1}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('threshold')}
                      value={thresholdValue}
                      onChange={(e) => setThresholdValue(e.target.value)}
                      slotProps={{ input: { startAdornment: <InputAdornment position="start">₹</InputAdornment> } }}
                    />
                  </GridItem>
                </GridContainer>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                  <Box>
                    <Typography fontWeight={600}>{t('hierarchyMode')}</Typography>
                  </Box>
                  <ToggleButtonGroup
                    value={hierarchyMode}
                    exclusive
                    size="small"
                    onChange={(_, v) => v && setHierarchyMode(v)}
                  >
                    <ToggleButton value="Sequential">{t('sequential')}</ToggleButton>
                    <ToggleButton value="AnyOne">{t('anyOne')}</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </CardContent>
            </Card>

            <Stack spacing={1.5}>
              {levels.map((level, idx) => (
                <Card key={idx} variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip label={`L${idx + 1}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                        <Typography fontWeight={600}>{level.name}</Typography>
                      </Stack>
                      <IconButton size="small" color="error" onClick={() => removeLevel(idx)} disabled={levels.length <= 1}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <GridContainer spacing={2} columns={12}>
                      <GridItem xs={12} sm={3}>
                        <TextField
                          select fullWidth size="small" label={t('approverType')}
                          value={level.approverType}
                          onChange={(e) => updateLevel(idx, { approverType: e.target.value as 'Role' | 'User', approverRoleId: null, approverUserId: null })}
                        >
                          <MenuItem value="Role">{t('approverRole')}</MenuItem>
                          <MenuItem value="User">{t('approverUser')}</MenuItem>
                        </TextField>
                      </GridItem>
                      <GridItem xs={12} sm={9}>
                        {level.approverType === 'Role' ? (
                          <TextField
                            select fullWidth size="small" label={t('selectRole')}
                            value={level.approverRoleId ?? ''}
                            onChange={(e) => updateLevel(idx, { approverRoleId: e.target.value })}
                          >
                            {roles.map((r) => (
                              <MenuItem key={r.id} value={r.id}>
                                {r.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            select fullWidth size="small" label={t('selectUser')}
                            value={level.approverUserId ?? ''}
                            onChange={(e) => updateLevel(idx, { approverUserId: e.target.value })}
                          >
                            {users.map((u) => (
                              <MenuItem key={u.id} value={u.id}>
                                {u.fullName}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      </GridItem>
                    </GridContainer>
                  </CardContent>
                </Card>
              ))}

              <Paper
                variant="outlined"
                onClick={addLevel}
                sx={{
                  py: 3, textAlign: 'center', cursor: 'pointer', borderStyle: 'dashed',
                  color: 'text.secondary', '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                }}
              >
                <Stack alignItems="center" spacing={0.5}>
                  <AddIcon fontSize="large" />
                  <Typography fontWeight={600}>{t('addLevel')}</Typography>
                </Stack>
              </Paper>
            </Stack>
          </Stack>
        </GridItem>

        <GridItem xs={12} md={4}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {t('flowPreview')}
                </Typography>
                <Stack spacing={1} alignItems="stretch">
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {transactionType}
                    </Typography>
                  </Paper>
                  {levels.map((l, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderColor: 'primary.main' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {idx + 1}. {l.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {levelLabel(l)}
                      </Typography>
                    </Paper>
                  ))}
                  <Paper sx={{ p: 1.5, bgcolor: 'success.main', color: 'success.contrastText' }}>
                    <Typography variant="body2" fontWeight={700}>
                      {t('authorized')}
                    </Typography>
                  </Paper>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {t('summary')}
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">{t('totalLevels')}</Typography>
                    <Typography fontWeight={600}>{levels.length}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography color="text.secondary">{t('autoNotify')}</Typography>
                    <Switch checked={autoNotify} onChange={(e) => setAutoNotify(e.target.checked)} size="small" />
                  </Stack>
                  <TextField
                    type="number" size="small" label={t('slaHours')} value={slaHours}
                    onChange={(e) => setSlaHours(Number(e.target.value))}
                  />
                  {existing && (
                    <FormControlLabel
                      control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                      label={t('activeToggle')}
                    />
                  )}
                </Stack>

                {missingHighValueL3 ? (
                  <Paper variant="outlined" sx={{ mt: 2, p: 1.5, bgcolor: 'error.light', borderColor: 'error.main' }}>
                    <Typography variant="caption">
                      High-value {transactionType} transactions currently lack a defined L3 auditor.
                    </Typography>
                  </Paper>
                ) : undefined}
              </CardContent>
            </Card>
          </Stack>
        </GridItem>
      </GridContainer>
    </>
  );
}
